import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { GlobalButton } from '@/components';
import {
  EDITABLE_INTEGRATION_REQUEST_STATUSES,
  IntegrationRequestStatus,
  OBSERVABLE_INTEGRATION_REQUEST_STATUSES,
  SENDABLE_STATUS_BY_TYPE,
  type IntegrationRequestType,
} from '../../../../models/IntegrationRequest';
import { useIsIntegrationIt } from '../../hooks';

interface DetailActionsProps {
  status: IntegrationRequestStatus;
  type: IntegrationRequestType;
  isBusy: boolean;
  isObservationOpen: boolean;
  hasIntegrationFailure: boolean;
  onGoToCommerce: () => void;
  onEdit: () => void;
  onCancelRequest: () => void;
  onObserve: () => void;
  onIntegrate: () => void;
  onRetryIntegration: () => void;
  onApproveDoc: () => void;
  onMarkSent: () => void;
}

interface ActionButton {
  key: string;
  label: string;
  primary: boolean;
  action: () => void;
}

const DetailActions = ({
  status,
  type,
  isBusy,
  isObservationOpen,
  hasIntegrationFailure,
  onGoToCommerce,
  onEdit,
  onCancelRequest,
  onObserve,
  onIntegrate,
  onRetryIntegration,
  onApproveDoc,
  onMarkSent,
}: DetailActionsProps) => {
  const isIt = useIsIntegrationIt();
  const canIntegrate = isIt && status === IntegrationRequestStatus.PENDING;
  const canRetry = isIt && status === IntegrationRequestStatus.IN_PROGRESS;
  const integrateLabel = type === 'API' ? 'Integrar' : 'Aplicar webhook';

  const buttons: ActionButton[] = [];
  if (EDITABLE_INTEGRATION_REQUEST_STATUSES.includes(status)) {
    buttons.push({ key: 'cancel', label: 'Cancelar solicitud', primary: false, action: onCancelRequest });
    buttons.push({ key: 'edit', label: 'Editar', primary: !canIntegrate, action: onEdit });
  }
  if (isIt && OBSERVABLE_INTEGRATION_REQUEST_STATUSES.includes(status) && !isObservationOpen) {
    buttons.push({ key: 'observe', label: 'Observar', primary: false, action: onObserve });
  }
  if (canIntegrate) {
    buttons.push({ key: 'integrate', label: integrateLabel, primary: true, action: onIntegrate });
  }
  if (canRetry) {
    buttons.push({
      key: 'retry',
      label: hasIntegrationFailure ? 'Reintentar' : 'Continuar',
      primary: true,
      action: onRetryIntegration,
    });
  }
  if (isIt && type === 'API' && status === IntegrationRequestStatus.INTEGRATED) {
    buttons.push({
      key: 'approve-doc',
      label: 'Aprobar documentación',
      primary: true,
      action: onApproveDoc,
    });
  }
  if (status === SENDABLE_STATUS_BY_TYPE[type]) {
    buttons.push({
      key: 'mark-sent',
      label: 'Marcar como enviado a cliente',
      primary: true,
      action: onMarkSent,
    });
  }

  return (
    <div className='sticky bottom-0 z-[1200] flex flex-wrap gap-2.5 justify-end border-t border-gray-200 bg-gray-50 px-6 py-4'>
      <GlobalButton variant={GlobalButtonVariant.textButton} action={onGoToCommerce}>
        Ir al comercio
      </GlobalButton>
      {buttons.map((button) => (
        <GlobalButton
          key={button.key}
          variant={
            button.primary ? GlobalButtonVariant.primaryButton : GlobalButtonVariant.secondaryButton
          }
          action={button.action}
          disabled={isBusy}
        >
          {button.label}
        </GlobalButton>
      ))}
    </div>
  );
};

export default DetailActions;
